import { useEffect, useState } from 'react';
import { Badge, Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { isApiError, type admin } from '@repletoryreach/sdk-api';
import { useUpdateWording } from '../../../../data/useLanguages';
import { useT } from '../../../../i18n/I18nProvider';
import { placeholderProblems } from '../languages';

const MUTED = 'var(--rr-color-text-muted)';

/** The service's refusal message, or the error's own. */
function refusalText(e: unknown, fallback: string): string {
  if (isApiError(e)) return (e.body as { error?: { message?: string } } | null)?.error?.message ?? e.message;
  return e instanceof Error ? e.message : fallback;
}

/** What a line is: as translated by machine, corrected here, still in English, or written in the code. */
function SourceBadge({ source }: { source: string }) {
  const t = useT();
  const colour = source === 'edited' ? 'blue' : source === 'missing' ? 'orange' : 'gray';
  return <Badge size="xs" variant="light" color={colour}>{t(`billing.languages.lines.source.${source}`, source)}</Badge>;
}

/**
 * One built-in line: the English as written beside the translation, edited in place. Saving keeps every `{{...}}`
 * part of the English and a button line within its length, as the service checks; "Use the machine translation"
 * takes the edit back; "Mark as read by a native speaker" records that someone who speaks it has read the line.
 * English and Afrikaans are written in the code, so their lines are shown and not edited.
 */
export function WordingRow({ code, languageName, line, readOnly }: {
  code: string;
  languageName: string;
  line: admin.LanguageWording;
  readOnly: boolean;
}) {
  const t = useT();
  const update = useUpdateWording(code);
  const [draft, setDraft] = useState(line.text ?? '');
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => { setDraft(line.text ?? ''); setRefusal(null); }, [line.text]);

  const text = draft.trim();
  const dirty = text !== (line.text ?? '').trim();
  const max = line.max_length ?? null;
  const problems = text ? placeholderProblems(line.english, text) : { missing: [], extra: [] };
  const localError = max !== null && text.length > max
    ? t('billing.languages.lines.tooLong', { max })
    : problems.missing.length > 0
      ? t('billing.languages.lines.lostParts', { parts: problems.missing.join(', ') })
      : problems.extra.length > 0
        ? t('billing.languages.lines.extraParts', { parts: problems.extra.join(', ') })
        : undefined;

  function save() {
    setRefusal(null);
    update.mutate({ key: line.key, change: { text } }, {
      onSuccess: () => notifySuccess(t('billing.languages.lines.saved', { key: line.key }), t('billing.languages.lines.titleShort')),
      onError: (e) => setRefusal(refusalText(e, t('common.unknownError')))
    });
  }

  function markRead() {
    update.mutate({ key: line.key, change: { reviewed: !line.reviewed } }, {
      onError: (e) => notifyError(refusalText(e, t('common.unknownError')), t('billing.languages.lines.titleShort'))
    });
  }

  function undoEdit() {
    openConfirmModal({
      title: t('billing.languages.lines.undo.title'),
      children: <Text role="bodySmall">{t('billing.languages.lines.undo.body', { key: line.key, name: languageName })}</Text>,
      labels: { confirm: t('billing.languages.lines.undo.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => update.mutate({ key: line.key, change: { text: '' } }, {
        onError: (e) => notifyError(refusalText(e, t('common.unknownError')), t('billing.languages.lines.titleShort'))
      })
    });
  }

  return (
    <div className="rr-list-row rr-languages-row" data-testid={`wording-${line.key}`}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text role="bodySmall" fw={600}><code>{line.key}</code></Text>
            {line.note && <Text role="caption" c={MUTED}>{line.note}</Text>}
          </Stack>
          <Group gap={4} wrap="wrap">
            <SourceBadge source={line.source} />
            {line.stale && <Badge size="xs" variant="light" color="orange">{t('billing.languages.lines.staleBadge')}</Badge>}
            {line.reviewed && !readOnly && <Badge size="xs" variant="light" color="green">{t('billing.languages.lines.readBadge')}</Badge>}
            {max !== null && <Badge size="xs" variant="outline" color="gray">{t('billing.languages.lines.button', { max })}</Badge>}
          </Group>
        </Group>
        <div className="rr-languages-line">
          <Stack gap={4}>
            <Text role="overline" c={MUTED}>{t('billing.languages.lines.english')}</Text>
            <div className="rr-languages-source"><Text role="bodySmall">{line.english}</Text></div>
          </Stack>
          {readOnly ? (
            <Stack gap={4}>
              <Text role="overline" c={MUTED}>{languageName}</Text>
              <div className="rr-languages-source"><Text role="bodySmall">{line.text ?? line.english}</Text></div>
            </Stack>
          ) : (
            <Textarea
              label={t('billing.languages.lines.in', { name: languageName })}
              value={draft}
              placeholder={line.english}
              autosize
              minRows={2}
              maxRows={8}
              onChange={(e) => { setDraft(e.currentTarget.value); setRefusal(null); }}
              description={max !== null
                ? t('billing.languages.lines.counter', { count: text.length, max })
                : line.source === 'missing' ? t('billing.languages.lines.missingHint') : undefined}
              error={refusal ?? localError}
              data-testid={`wording-text-${line.key}`}
            />
          )}
        </div>
        {!readOnly && (
          <Group justify="flex-end" gap="xs" wrap="wrap">
            {line.source === 'edited' && (
              <Button size="xs" variant="subtle" disabled={update.isPending} onClick={undoEdit}>{t('billing.languages.lines.undo.action')}</Button>
            )}
            <Button
              size="xs"
              variant="outline"
              disabled={line.source === 'missing' || dirty || update.isPending}
              title={line.source === 'missing' ? t('billing.languages.lines.readNeedsText') : undefined}
              onClick={markRead}
              data-testid={`wording-read-${line.key}`}
            >
              {line.reviewed ? t('billing.languages.lines.markUnread') : t('billing.languages.lines.markRead')}
            </Button>
            <Button size="xs" disabled={!dirty || !text || Boolean(localError)} loading={update.isPending && dirty} onClick={save}>
              {t('action.save')}
            </Button>
          </Group>
        )}
      </Stack>
    </div>
  );
}
